//Package and Product data utilities.
//Shared types and functions for fetching package/COA data and product details.

import { getPackagesStagedTotals, getPackagesTotals } from '../hooks/usePackages';
import { getProduct } from '../hooks/useProducts';
import { normalizeCoaKeys } from './coaUtils';
import { withCanonicalDateAliases } from '../utils/printDates';

//Types
export type PackageCoaOption = {
  labTestingStateName: any;
  id: string;
  label: string;
  facilityName: string;
  facilityLicense: string;
  facilityId: number | string | null;
  coaData: Record<string, any>;
  productId?: number;
  metrcPackageId?: number | null;
  marketCode: string;
};

export type Product = {
  id: number;
  organizationId: number;
  brandId?: number;
  productImageId?: number;
  productImageUrl?: string;
  name: string;
  productCategoryId: number;
  productCategoryName: string;
  inventoryUpc: string;
  strain: string;
  subspeciesId?: number;
  description: string;
  notes: string;
  active: boolean;
  marketIds: number[];
  shelfLifeDays?: number | null;
};

// Staged labels are not unique across facilities or print snapshots.
// Prefer the row identity so initial and searched options agree across ordering.
export const getStagedPackageOptionId = (pkg: any, packageLabel: string, index: number): string => {
  const rowId = pkg.id ?? pkg.Id;
  if (rowId !== undefined && rowId !== null && String(rowId).trim() !== '') {
    return `staged-${String(rowId).trim()}`;
  }

  // Older responses may omit IDs. Preserve every row, including same-facility
  // snapshots; JSON encoding avoids collisions from delimiters in labels.
  return `staged-fallback-${JSON.stringify([
    packageLabel,
    pkg.tagId ?? pkg.TagId ?? null,
    pkg.facilityId ?? pkg.FacilityId ?? null,
    pkg.productId ?? pkg.ProductId ?? null,
    pkg.sourcePackageId ?? pkg.SourcePackageId ?? null,
    pkg.printRunId ?? pkg.PrintRunId ?? null,
    pkg.createdAt ?? pkg.CreatedAt ?? null,
    index,
  ])}`;
};

//Fetch Utilities
export async function fetchPackagesWithCoa(
  organizationId: number,
  marketIds?: number[],
  options?: {
    logPrefix?: string;
    totalsPageSize?: number;
    coaPageSize?: number;
    skipCoaFetch?: boolean;
  }
): Promise<PackageCoaOption[]> {
  const logPrefix = options?.logPrefix || '[packageUtils]';
  const totalsPageSize = options?.totalsPageSize || 50;
  const skipCoaFetch = options?.skipCoaFetch ?? true;

  const packageMap = new Map<string, PackageCoaOption>();

  try {
    const totalsResponse = await getPackagesTotals({
      organizationId,
      page: 0,
      pageSize: totalsPageSize,
    });

    const packagesData = totalsResponse?.data || totalsResponse || [];
    (Array.isArray(packagesData) ? packagesData : []).forEach((pkg: any) => {
      const pkgId = String(pkg.id || pkg.packageId || pkg.PackageId);
      if (!pkgId || pkgId === 'undefined') return;

      packageMap.set(pkgId, {
        id: pkgId,
        label: pkg.label || `Package ${pkgId}`,
        facilityId: pkg.facilityId ?? pkg.FacilityId ?? null,
        facilityName: pkg.facilityName ?? pkg.FacilityName ?? '',
        facilityLicense:
          pkg.facilityLicense ??
          pkg.FacilityLicense ??
          pkg.license ??
          pkg.License ??
          '',
        productId: pkg.productId || pkg.ProductId || undefined,
        metrcPackageId: pkg.metrcPackageId ?? pkg.MetrcPackageId ?? null,
        labTestingStateName: pkg.labTestingStateName || '',
        coaData: {
          ...withCanonicalDateAliases(normalizeCoaKeys(pkg)),
          packageTag: pkg.label || '',
          productName: pkg.productName || '',
          facilityName: pkg.facilityName || pkg.FacilityName || pkg.facility?.name || pkg.Facility?.Name || '',
          facilityLicense: pkg.facilityLicense || pkg.FacilityLicense || pkg.license || pkg.License || '',
          strain: pkg.strain || '',
          productCategoryName: pkg.productCategoryName || '',
          quantity: pkg.quantity || 0,
          facilityId: pkg.facilityId,
          labTestingStateName: pkg.labTestingStateName || '',
        },
        marketCode: pkg.marketCode || '',
      });
    });
  } catch (err) {
    console.error(`${logPrefix} Failed to fetch packages from totals:`, err);
  }

  try {
    const stagedResponse = await getPackagesStagedTotals({
      organizationId,
      page: 0,
      pageSize: totalsPageSize,
    });

    const stagedData = stagedResponse?.data || stagedResponse || [];
    (Array.isArray(stagedData) ? stagedData : []).forEach((pkg: any, index: number) => {
      const pkgLabel = pkg.packageLabel || pkg.PackageLabel || pkg.label;
      if (!pkgLabel) return;

      let coaData: Record<string, any> = {
        packageTag: pkgLabel,
        productName: pkg.productName || pkg.ProductName || '',
        strain: '',
        productCategoryName: '',
        quantity: 0,
        facilityId: pkg.facilityId || pkg.FacilityId,
        labTestingStateName: '',
        marketCode: '',
      };

      let rawData = pkg.data || pkg.Data;
      if (rawData) {
        try {
          if (typeof rawData === 'string') {
            rawData = JSON.parse(rawData);
          }
          if (typeof rawData === 'string') {
            rawData = JSON.parse(rawData);
          }
          coaData = { ...coaData, ...(withCanonicalDateAliases(normalizeCoaKeys(rawData)) || {}) };
        } catch {
          // Data may not be parseable.
        }
      }

      const optionId = getStagedPackageOptionId(pkg, pkgLabel, index);
      packageMap.set(optionId, {
        id: optionId,
        label: `[Staged] ${pkgLabel}`,
        facilityId: pkg.facilityId ?? pkg.FacilityId ?? null,
        facilityName: pkg.facilityName ?? pkg.FacilityName ?? '',
        facilityLicense:
          pkg.facilityLicense ??
          pkg.FacilityLicense ??
          pkg.license ??
          pkg.License ??
          '',
        productId: pkg.productId || pkg.ProductId || undefined,
        labTestingStateName: coaData.labTestingStateName || coaData.labTestingState || '',
        marketCode: coaData.marketCode || '',
        coaData,
      });
    });
  } catch (err) {
    console.error(`${logPrefix} Failed to fetch staged packages:`, err);
  }

  if (!skipCoaFetch && marketIds?.length) {
    const coaResponses = await Promise.all(
      marketIds.map((_marketId) => Promise.resolve([]))
    );

    coaResponses.flat().forEach((pkg: any) => {
      const pkgData = pkg.result || pkg;
      const pkgId = String(pkgData.packageId || pkgData.PackageId);
      if (!pkgId || pkgId === 'undefined') return;

      try {
        let rawData = pkgData.data || pkgData.Data || '{}';
        if (typeof rawData === 'string') {
          rawData = JSON.parse(rawData);
          if (typeof rawData === 'string') {
            rawData = JSON.parse(rawData);
          }
        }
        const coaData = withCanonicalDateAliases(normalizeCoaKeys(rawData));

        const existing = packageMap.get(pkgId);
        if (existing) {
          existing.coaData = {
            ...existing.coaData,
            ...coaData,
            packageTag: existing.label || coaData.packageTag || '',
          };
        } else {
          const packageTag =
            pkgData.label || pkgData.Label || coaData.packageTag || coaData.Label || '';
          const label = packageTag || coaData.productName || `Package ${pkgId}`;

          if (packageTag && !coaData.packageTag) {
            coaData.packageTag = packageTag;
          }

          packageMap.set(pkgId, {
            id: pkgId,
            label,
            facilityId: pkgData.facilityId ?? pkgData.FacilityId ?? null,
            facilityName: pkgData.facilityName ?? pkgData.FacilityName ?? '',
            facilityLicense:
              pkgData.facilityLicense ??
              pkgData.FacilityLicense ??
              pkgData.license ??
              pkgData.License ??
              '',
            labTestingStateName: coaData.labTestingStateName || '',
            coaData,
            marketCode: pkgData.marketCode || pkgData.MarketCode || '',
          });
        }
      } catch (parseErr) {
        console.error(`${logPrefix} Failed to parse COA data for package ${pkgId}:`, parseErr);
      }
    });
  }

  return Array.from(packageMap.values());
}


export async function fetchProduct(
  organizationId: number,
  productId: number,
  logPrefix = '[packageUtils]'
): Promise<Product | null> {
  try {
    return await getProduct(organizationId, productId) as unknown as Product;
  } catch (err) {
    console.error(`${logPrefix} Failed to fetch product ${productId}:`, err);
    return null;
  }
}
